import {
  customElement,
  type TemplateResult,
  property,
  state,
} from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { demoFunc } from './demo.js';
import { datepickerStyles } from './styles.js';
import { renderDatepicker } from './template.js';
import { DeesInputDatepickerPopup } from './datepicker-popup.js';
import { createCalendarDate, dateValidationMessage, parseDateFields, parseDateValue } from './date-validation.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-layout/dees-label/dees-label.js';


declare global {
  interface HTMLElementTagNameMap {
    'dees-input-datepicker': DeesInputDatepicker;
  }
}

@customElement('dees-input-datepicker')
export class DeesInputDatepicker extends DeesInputBase<DeesInputDatepicker> {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  @property({ type: String })
  accessor value: string = '';

  @property({ type: Boolean })
  accessor enableTime: boolean = false;

  @property({ type: String })
  accessor timeFormat: '24h' | '12h' = '24h';

  @property({ type: Number })
  accessor minuteIncrement: number = 1;

  @property({ type: String })
  accessor dateFormat: string = 'YYYY-MM-DD';

  @property({ type: String })
  accessor minDate: string = '';

  @property({ type: String })
  accessor maxDate: string = '';

  @property({ type: Array })
  accessor disabledDates: string[] = [];

  @property({ type: Number })
  accessor weekStartsOn: 0 | 1 = 1;

  @property({ type: String })
  accessor placeholder: string = 'YYYY-MM-DD';

  @property({ type: Boolean })
  accessor enableTimezone: boolean = false;

  @property({ type: String })
  accessor timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  @property({ type: Array })
  accessor events: import('./types.js').IDateEvent[] = [];

  @state()
  accessor isOpened: boolean = false;

  @state()
  accessor selectedDate: Date | null = null;

  @state()
  accessor viewDate: Date = new Date();

  @state()
  accessor selectedHour: number = 0;

  @state()
  accessor selectedMinute: number = 0;

  /** Uncommitted text stays visible even when it cannot be represented by value. */
  @state()
  accessor draftText: string | null = null;

  @state()
  accessor validationText = '';

  public get validationState(): 'invalid' | 'valid' { return this.validationText ? 'invalid' : 'valid'; }

  private lastCommittedValue: string | undefined;

  private popupInstance: DeesInputDatepickerPopup | null = null;

  public static styles = datepickerStyles;

  public render(): TemplateResult {
    return renderDatepicker(this);
  }

  public willUpdate(changed: Map<PropertyKey, unknown>): void {
    super.willUpdate(changed);
    if (changed.has('value') && this.value !== this.lastCommittedValue) {
      this.syncValue(this.value);
    }
  }

  public updated(changed: Map<string, unknown>): void {
    super.updated(changed);
    if (changed.has('disabled') && this.disabled) this.closePopup();
    if (['value', 'minDate', 'maxDate', 'disabledDates', 'required', 'disabled'].some(key => changed.has(key))) {
      this.updateValidation(this.currentValidationMessage());
    }
    if (this.popupInstance) {
      this.popupInstance.minDate = this.minDate;
      this.popupInstance.maxDate = this.maxDate;
      this.popupInstance.disabledDates = this.disabledDates;
      if (changed.has('selectedDate')) {
        this.popupInstance.selectedDate = this.selectedDate;
        this.popupInstance.viewDate = new Date(this.viewDate);
        this.popupInstance.selectedHour = this.selectedHour;
        this.popupInstance.selectedMinute = this.selectedMinute;
      }
    }
    const input = this.shadowRoot?.querySelector('input');
    input?.setCustomValidity(this.disabled ? '' : this.validationText);
    if (input && (changed.has('draftText') || changed.has('value'))) {
      const display = this.draftText ?? this.formatDate(this.value);
      if (input.value !== display) input.value = display;
    }
  }

  private syncValue(value: string | null): void {
    this.value = value ?? '';
    this.lastCommittedValue = this.value;
    this.draftText = null;
    this.selectedDate = parseDateValue(this.value);
    this.viewDate = new Date(this.selectedDate ?? new Date());
    this.selectedHour = this.selectedDate?.getHours() ?? 0;
    this.selectedMinute = this.selectedDate?.getMinutes() ?? 0;
  }

  public formatDate(isoString: string): string {
    if (!isoString) return '';

    try {
      const date = parseDateValue(isoString);
      if (!date) return isoString;

      let formatted = this.dateFormat;
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().padStart(4, '0');

      formatted = formatted.replace('YYYY', year);
      formatted = formatted.replace('YY', year.slice(-2));
      formatted = formatted.replace('MM', month);
      formatted = formatted.replace('DD', day);

      if (this.enableTime) {
        const hours24 = date.getHours();
        const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours24 >= 12 ? 'PM' : 'AM';

        if (this.timeFormat === '12h') {
          formatted += ` ${hours12}:${minutes} ${ampm}`;
        } else {
          formatted += ` ${hours24.toString().padStart(2, '0')}:${minutes}`;
        }
      }

      if (this.enableTimezone) {
        const formatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone: this.timezone });
        const parts = formatter.formatToParts(date);
        const tzPart = parts.find(part => part.type === 'timeZoneName');
        if (tzPart) formatted += ` ${tzPart.value}`;
      }

      return formatted;
    } catch {
      return '';
    }
  }

  public async toggleCalendar(): Promise<void> {
    if (this.disabled) return;

    if (this.isOpened) {
      this.closePopup();
      return;
    }

    this.isOpened = true;

    const inputContainer = this.shadowRoot!.querySelector('.input-container') as HTMLElement;
    const rect = inputContainer.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const opensToTop = spaceBelow < 400 && spaceAbove > spaceBelow;

    if (!this.popupInstance) {
      this.popupInstance = document.createElement('dees-input-datepicker-popup');
    }

    // Configure popup
    this.popupInstance.triggerRect = rect;
    this.popupInstance.ownerComponent = this;
    this.popupInstance.opensToTop = opensToTop;
    this.popupInstance.enableTime = this.enableTime;
    this.popupInstance.timeFormat = this.timeFormat;
    this.popupInstance.minuteIncrement = this.minuteIncrement;
    this.popupInstance.weekStartsOn = this.weekStartsOn;
    this.popupInstance.minDate = this.minDate;
    this.popupInstance.maxDate = this.maxDate;
    this.popupInstance.disabledDates = this.disabledDates;
    this.popupInstance.enableTimezone = this.enableTimezone;
    this.popupInstance.timezone = this.timezone;
    this.popupInstance.events = this.events;
    this.popupInstance.selectedDate = this.selectedDate;
    this.popupInstance.viewDate = new Date(this.viewDate);
    this.popupInstance.selectedHour = this.selectedHour;
    this.popupInstance.selectedMinute = this.selectedMinute;

    // Listen for popup events
    this.popupInstance.addEventListener('date-selected', this.handleDateSelected);
    this.popupInstance.addEventListener('date-cleared', this.handleDateCleared);
    this.popupInstance.addEventListener('close-request', this.handleCloseRequest);
    this.popupInstance.addEventListener('reposition-request', this.handleRepositionRequest);

    await this.popupInstance.show();
  }

  private closePopup(): void {
    this.isOpened = false;
    const popup = this.popupInstance;
    this.popupInstance = null;
    if (popup) {
      popup.removeEventListener('date-selected', this.handleDateSelected);
      popup.removeEventListener('date-cleared', this.handleDateCleared);
      popup.removeEventListener('close-request', this.handleCloseRequest);
      popup.removeEventListener('reposition-request', this.handleRepositionRequest);
      void popup.hide();
    }
  }

  private handleDateSelected = (event: Event): void => {
    if (this.disabled) return;
    this.commitDate((event as CustomEvent<Date>).detail, true);
  };

  private handleDateCleared = (): void => {
    if (!this.disabled) this.commitClear();
  };

  private commitDate(date: Date, normalizeDraft: boolean): boolean {
    const message = dateValidationMessage(date, this);
    if (message) { this.updateValidation(message); return false; }
    const value = this.formatValueWithTimezone(date);
    const changed = value !== this.value;
    this.lastCommittedValue = value;
    this.value = value;
    this.selectedDate = new Date(date);
    this.selectedHour = date.getHours();
    this.selectedMinute = date.getMinutes();
    this.viewDate = new Date(date);
    if (normalizeDraft) this.draftText = null;
    this.updateValidation('');
    if (changed) this.changeSubject.next(this);
    return true;
  }

  private commitClear(): void {
    const changed = Boolean(this.value);
    this.syncValue('');
    this.updateValidation(this.currentValidationMessage());
    if (changed) this.changeSubject.next(this);
  }

  private handleCloseRequest = (): void => {
    this.closePopup();
  };

  private handleRepositionRequest = (): void => {
    if (!this.popupInstance || !this.isOpened) return;
    const inputContainer = this.shadowRoot!.querySelector('.input-container') as HTMLElement;
    if (!inputContainer) return;

    const rect = inputContainer.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      this.closePopup();
      return;
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    this.popupInstance.opensToTop = spaceBelow < 400 && spaceAbove > spaceBelow;
    this.popupInstance.triggerRect = rect;
  };

  public handleKeydown(e: KeyboardEvent): void {
    if (this.disabled || e.isComposing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (this.draftText !== null) this.commitManualInput(true);
      else void this.toggleCalendar();
    } else if (e.key === 'ArrowDown' && e.altKey) {
      e.preventDefault();
      if (!this.isOpened) void this.toggleCalendar();
    } else if (e.key === 'Escape' && this.isOpened) {
      e.preventDefault();
      e.stopPropagation();
      this.closePopup();
    }
  }

  public focus(optionsArg?: FocusOptions): void {
    this.shadowRoot?.querySelector('input')?.focus(optionsArg);
  }

  public blur(): void {
    this.shadowRoot?.querySelector('input')?.blur();
  }

  public clearValue(e: Event): void {
    e.stopPropagation();
    if (!this.disabled) this.commitClear();
  }

  public handleManualInput(e: InputEvent): void {
    if (this.disabled) return;
    this.draftText = (e.target as HTMLInputElement).value;
    if (!e.isComposing) this.updateValidation(this.currentValidationMessage());
  }

  public handleInputBlur(_event: FocusEvent): void {
    if (!this.disabled && this.draftText !== null) this.commitManualInput(true);
  }

  private commitManualInput(normalizeDraft: boolean): void {
    const text = this.draftText?.trim() ?? '';
    if (!text) { this.commitClear(); return; }
    const date = this.parseManualDate(text);
    const message = dateValidationMessage(date, this);
    if (message) { this.updateValidation(message); return; }
    this.commitDate(date!, normalizeDraft);
  }

  private parseManualDate(input: string): Date | null {
    const match = /^(\S+)(?:\s+(\d{1,2}):(\d{2})(?:\s+(AM|PM))?)?$/i.exec(input.trim());
    if (!match) return null;
    const fields = parseDateFields(match[1]);
    if (!fields) return null;
    let hour = Number(match[2] ?? 0);
    const minute = Number(match[3] ?? 0);
    if (match[4]) {
      if (hour < 1 || hour > 12) return null;
      hour = hour % 12 + (match[4].toUpperCase() === 'PM' ? 12 : 0);
    }
    return createCalendarDate(...fields, match[2] === undefined ? undefined : hour, minute);
  }

  private currentValidationMessage(): string {
    if (this.disabled) return '';
    const text = this.draftText?.trim();
    if (this.draftText !== null) {
      if (!text) return this.required ? 'Choose a date.' : '';
      return dateValidationMessage(this.parseManualDate(text), this);
    }
    if (!this.value) return this.required ? 'Choose a date.' : '';
    return dateValidationMessage(parseDateValue(this.value), this);
  }

  private updateValidation(message: string): void {
    const changed = message !== this.validationText;
    this.validationText = message;
    this.shadowRoot?.querySelector('input')?.setCustomValidity(message);
    if (changed) this.dispatchEvent(new CustomEvent('validation-change', {
      detail: { valid: !message, message }, bubbles: true, composed: true,
    }));
  }

  public checkValidity(): boolean { return !this.currentValidationMessage(); }

  public reportValidity(): boolean {
    const message = this.currentValidationMessage();
    this.updateValidation(message);
    if (message) this.focus();
    return !message;
  }

  public reset(): void { this.setValue(''); }

  private formatValueWithTimezone(date: Date): string {
    if (!this.enableTimezone) return date.toISOString();

    const formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false, timeZone: this.timezone, timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(date);
    const dateParts: any = {};
    parts.forEach(part => { dateParts[part.type] = part.value; });

    const isoString = `${dateParts.year}-${dateParts.month}-${dateParts.day}T${dateParts.hour}:${dateParts.minute}:${dateParts.second}`;
    const tzOffset = this.getTimezoneOffset(date, this.timezone);
    return `${isoString}${tzOffset}`;
  }

  private getTimezoneOffset(date: Date, timezone: string): string {
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
    const minutes = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';
    return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  public getValue(): string {
    return this.value;
  }

  public setValue(value: string | null): void {
    this.syncValue(value);
    this.updateValidation(this.currentValidationMessage());
  }

  async disconnectedCallback() {
    this.closePopup();
    await super.disconnectedCallback();
  }
}
